import { and, asc, eq, isNull } from "drizzle-orm";
import {
  bill_of_materials,
  bom_component,
  inventory_count,
  inventory_count_line,
  landed_cost,
  landed_cost_line,
  location,
  lot,
  manufacturing_order,
  type PowerSyncDrizzleDB,
  product,
  product_category,
  product_supplier,
  purchase_order,
  purchase_order_line,
  purchase_return,
  purchase_return_line,
  reorder_rule,
  stock_move,
  stock_move_line,
  stock_picking,
  stock_quant,
  uom,
  valuation_layer,
  vendor,
  warehouse,
} from "@stockpilot/powersync";
import { generateId } from "@fleet/utils";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  readMatchingRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import {
  aggregateQuantDeltas,
  computeLineQuantDeltas,
  type PickingKind,
  type QuantEffect,
  resolveLineLocations,
} from "./operations";
import {
  canConfirm,
  canReceive,
  type IdentifiedPoLine,
  planReceipt,
  resolvePoStateAfterReceipt,
} from "./purchases";
import { type CountLineLike, planCountAdjustments } from "./counts";
import {
  type BomComponentLike,
  canProduce,
  planComponentConsumption,
  producedUnitCost,
} from "./manufacturing";
import { allocateLandedCost, canApplyLandedCost, type LandedCostBase } from "./landed-cost";
import { canConfirmReturn, planReturnEffects, type ReturnLineLike } from "./returns";
import { applyMove, QTY_SCALE } from "./units";
import { applyInbound, type AvcoState, averageUnitCost, valueOf } from "./valuation";

/**
 * ── The phantom UPDATE in this file ─────────────────────────────────────────
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync view can match ZERO
 * rows and return "success" while the newly created row is still traveling to
 * the server (canon `listto-app` 9ae347b6, details in
 * `../powersync/verified-write.ts`). In a local-first ERP, that is a count or
 * stock adjustment the user believes they saved: inventory becomes unbalanced
 * without a trace. Every patch-by-id mutation in this file goes through
 * `patchRowVerified`/`waitForRowPresent`.
 *
 * Second class, specific to `stock_quant`: several mutations decide
 * INSERT-vs-UPDATE for on-hand with their own SELECT by product×location×lot
 * (there is no known ID beforehand). That SELECT falls into the SAME window — if
 * the quant exists but the checkpoint has not brought it back yet, the SELECT
 * sees it as absent and INSERTs a new row: two quants for the same
 * product×location×lot, with on-hand unbalanced and no visible error. Those
 * mutations resolve existence with `readMatchingRowVerified` BEFORE opening the
 * transaction (never inside: it would sleep with the write lock held), in
 * parallel when touching several lines so the cost is not multiplied by the
 * number of lines.
 */

// ─── PRODUCTS ────────────────────────────────────────────────────────────────

export interface CreateProductInput {
  name: string;
  sku?: string | null;
  kind?: string;
  categoryId?: string | null;
  uomId?: string | null;
  barcode?: string | null;
  stdCost?: number;
  /** Tracking: 'none' | 'lot' | 'serial' (Stockpilot F4). */
  tracking?: string;
}

export async function createProductMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateProductInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(product).values({
    id,
    organization_id: orgId,
    name: input.name.trim(),
    sku: input.sku?.trim() ?? null,
    kind: input.kind ?? "storable",
    category_id: input.categoryId ?? null,
    uom_id: input.uomId ?? null,
    barcode: input.barcode?.trim() ?? null,
    std_cost: input.stdCost ?? 0,
    active: 1,
    tracking: input.tracking ?? "none",
    // The server has `product.is_kit` NOT NULL (F5 migration). The sync
    // write-path sends this column explicitly, so a local NULL rejects the
    // entire batch ("null value in column is_kit ... violates not-null"). A
    // product created through basic onboarding is never a kit → 0.
    is_kit: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    sku: string | null;
    kind: string;
    category_id: string | null;
    uom_id: string | null;
    barcode: string | null;
    std_cost: number;
    active: number;
    tracking: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, product, product.id, id, finalUpdates, options);
}

/**
 * Sends a product to trash (L2, soft-delete). The server records it as
 * `deleted`; restore = clear deleted_at. There is no purge (the write-path also
 * converts any hard DELETE into a soft-delete).
 */
export async function deleteProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, product, product.id, id, { deleted_at: nowISO() }, options);
}

/** Restores a product from trash. */
export async function restoreProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, product, product.id, id, { deleted_at: null }, options);
}

// ─── CATEGORIES / UoM ─────────────────────────────────────────────────────────

export async function createCategoryMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  parentId?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(product_category).values({
    id,
    organization_id: orgId,
    parent_id: parentId ?? null,
    name: name.trim(),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function createUomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  opts?: { category?: string; factor?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(uom).values({
    id,
    organization_id: orgId,
    name: name.trim(),
    category: opts?.category ?? null,
    factor: opts?.factor ?? 1000,
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── WAREHOUSES / LOCATIONS ───────────────────────────────────────────────────

export async function createWarehouseMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  code?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(warehouse).values({
    id,
    organization_id: orgId,
    name: name.trim(),
    code: code?.trim() ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function createLocationMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: {
    name: string;
    warehouseId?: string | null;
    parentId?: string | null;
    code?: string | null;
    usage?: string;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(location).values({
    id,
    organization_id: orgId,
    warehouse_id: input.warehouseId ?? null,
    parent_id: input.parentId ?? null,
    name: input.name.trim(),
    code: input.code?.trim() ?? null,
    usage: input.usage ?? "internal",
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── STOCK: INVENTORY ADJUSTMENT ──────────────────────────────────────────────

/**
 * Adjusts a product's on-hand at a location. Creates an append-only stock_move
 * with the delta and updates/creates the stock_quant (derived projection).
 * CC-7: blocking (await) in a single transaction.
 *
 * @param targetQty target COUNTED quantity, in minor units (integer).
 * @param reason    adjustment reason, REQUIRED (ERP requirement §1b: loss/
 *                  breakage/theft/count/other — the server rejects moves without
 *                  a reason). See `ADJUSTMENT_REASONS`.
 * @param lotId     optional lot (F4): scopes on-hand by (product×location×lot).
 * @param note      optional free text accompanying the reason.
 */
export async function adjustStockMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  productId: string,
  locationId: string,
  targetQty: number,
  reason: string,
  lotId?: string | null,
  note?: string | null,
  options?: VerifiedWriteOptions,
): Promise<{ moveId: string; newOnHand: number }> {
  const now = nowISO();
  const moveId = generateId();
  const lotRef = lotId ?? null;

  // Verified (not a bare SELECT): if this quant exists but has just reappeared
  // after a checkpoint rebuild, a SELECT without retries would see it as absent
  // and the `else` below would INSERT a second quant for the same
  // product×location×lot — a real imbalance; see the file header.
  const existing = await readMatchingRowVerified(
    () =>
      db
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotRef)),
    options,
  );

  const currentOnHand = existing?.qty ?? 0;
  const delta = targetQty - currentOnHand;
  const newOnHand = applyMove(currentOnHand, delta);

  await db.transaction(async (trx) => {
    await trx.insert(stock_move).values({
      id: moveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: null,
      dst_location_id: locationId,
      qty: delta,
      reason,
      note: note?.trim() || null,
      lot_id: lotRef,
      created_at: now,
      updated_at: now,
    });

    if (existing) {
      await trx
        .update(stock_quant)
        .set({ qty: newOnHand, updated_at: now })
        .where(eq(stock_quant.id, existing.id));
    } else {
      await trx.insert(stock_quant).values({
        id: generateId(),
        organization_id: orgId,
        product_id: productId,
        location_id: locationId,
        qty: newOnHand,
        lot_id: lotRef,
        created_at: now,
        updated_at: now,
      });
    }
  });

  return { moveId, newOnHand };
}

/**
 * Quant filter by (product, location, lot). A null lot is treated as "no lot"
 * (untracked products): this keeps untracked on-hand from mixing with per-lot
 * on-hand for the same product/location.
 */
function quantMatch(productId: string, locationId: string, lotId: string | null) {
  return and(
    eq(stock_quant.product_id, productId),
    eq(stock_quant.location_id, locationId),
    lotId === null ? isNull(stock_quant.lot_id) : eq(stock_quant.lot_id, lotId),
  );
}

/** Minimal move shape needed to record its counter-move. */
export interface ReversibleMove {
  id: string;
  organization_id: string | null;
  product_id: string | null;
  src_location_id: string | null;
  dst_location_id: string | null;
  qty: number | null;
  lot_id: string | null;
}

/**
 * CORRECTION of a confirmed move (domain criterion §1b): moves are NOT edited,
 * deleted, or undone — the correction is an explicit REVERSE MOVE WITH a reason,
 * recorded in history like any other movement. It reverses source↔destination
 * (or negates the delta for single-location adjustments) and reconciles the
 * `stock_quant` projection(s) in the same transaction.
 */
export async function reverseStockMoveMutation(
  db: PowerSyncDrizzleDB,
  original: ReversibleMove,
  reason: string,
  note?: string | null,
  options?: VerifiedWriteOptions,
): Promise<{ moveId: string }> {
  const productId = original.product_id;
  // The counter-move lives in the SAME organization as the original (it may be
  // another member's org, not the corrector's personal one).
  const orgId = original.organization_id;
  if (!productId || !orgId) {
    throw new Error("Cannot reverse a move without product/organization");
  }
  const qty = original.qty ?? 0;
  const lotRef = original.lot_id ?? null;
  const now = nowISO();
  const moveId = generateId();

  // Quant effects: undo what the original move applied.
  // Adjustment (one location): negate the delta at that location.
  // Transfer (src→dst): leave dst and return to src.
  const effects: { locationId: string; delta: number }[] = [];
  if (original.dst_location_id) {
    effects.push({ locationId: original.dst_location_id, delta: -qty });
  }
  if (original.src_location_id) {
    effects.push({ locationId: original.src_location_id, delta: qty });
  }

  // Shape of the reverse move: an ADJUSTMENT (one endpoint) is reversed by
  // negating the delta at the SAME location (the feed shows "−5 (count)"); a
  // TRANSFER is reversed by swapping source↔destination with the same qty.
  const isAdjustment = !original.src_location_id || !original.dst_location_id;
  const inverse = isAdjustment
    ? {
        src: original.src_location_id ?? null,
        dst: original.dst_location_id ?? null,
        qty: -qty,
      }
    : {
        src: original.dst_location_id ?? null,
        dst: original.src_location_id ?? null,
        qty,
      };

  // Pre-flight OUTSIDE the transaction: every quant that `effects` will touch
  // is reread with retries, in parallel (total cost is bounded by the worst
  // case, not multiplied by the number of effects). Inside the transaction it
  // cannot sleep while waiting (the write lock is held), so the wait happens
  // here; the internal SELECT remains unchanged.
  await Promise.all(
    effects.map(({ locationId }) =>
      readMatchingRowVerified(
        () =>
          db
            .select({ id: stock_quant.id })
            .from(stock_quant)
            .where(quantMatch(productId, locationId, lotRef)),
        options,
      ),
    ),
  );

  await db.transaction(async (trx) => {
    await trx.insert(stock_move).values({
      id: moveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: inverse.src,
      dst_location_id: inverse.dst,
      qty: inverse.qty,
      reason,
      note: note?.trim() || null,
      lot_id: lotRef,
      created_at: now,
      updated_at: now,
    });

    for (const { locationId, delta } of effects) {
      const [existing] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotRef));
      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateId(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: applyMove(0, delta),
          lot_id: lotRef,
          created_at: now,
          updated_at: now,
        });
      }
    }
  });

  return { moveId };
}

// ─── OPERATIONS (Stockpilot F2) ──────────────────────────────────────────────

export interface CreatePickingInput {
  kind: PickingKind;
  reference?: string | null;
  warehouseId?: string | null;
  srcLocationId?: string | null;
  dstLocationId?: string | null;
  note?: string | null;
}

/** Creates an operation in draft state. */
export async function createPickingMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePickingInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(stock_picking).values({
    id,
    organization_id: orgId,
    kind: input.kind,
    state: "draft",
    reference: input.reference?.trim() ?? null,
    warehouse_id: input.warehouseId ?? null,
    src_location_id: input.srcLocationId ?? null,
    dst_location_id: input.dstLocationId ?? null,
    note: input.note?.trim() ?? null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Adds a line to a draft operation. Resolves source/destination from the
 * operation type and the document's default locations.
 */
export async function addPickingLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  picking: {
    id: string;
    kind: PickingKind;
    src_location_id: string | null;
    dst_location_id: string | null;
  },
  productId: string,
  qty: number,
  lotId?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  const { srcLocationId, dstLocationId } = resolveLineLocations(
    picking.kind,
    picking.src_location_id,
    picking.dst_location_id,
  );
  await db.insert(stock_move_line).values({
    id,
    organization_id: orgId,
    picking_id: picking.id,
    product_id: productId,
    src_location_id: srcLocationId,
    dst_location_id: dstLocationId,
    qty,
    lot_id: lotId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a line from a draft operation. */
export async function deletePickingLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(stock_move_line).where(eq(stock_move_line.id, lineId));
}

interface ConfirmablePicking {
  id: string;
  kind: PickingKind;
  state: string | null;
}

interface ConfirmableLine {
  product_id: string | null;
  src_location_id: string | null;
  dst_location_id: string | null;
  qty: number | null;
  lot_id?: string | null;
}

/**
 * Confirms an operation (draft→done): materializes a stock_move for each quant
 * effect of each line and updates derived on-hand (stock_quant). CC-7: blocking
 * (await) in a single transaction. Idempotent: if it is already 'done', does
 * nothing.
 */
export async function confirmPickingMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  picking: ConfirmablePicking,
  lines: ConfirmableLine[],
  options?: VerifiedWriteOptions,
): Promise<{ confirmed: boolean; moveCount: number }> {
  if (picking.state === "done") {
    return { confirmed: false, moveCount: 0 };
  }

  // 1) Build effects by (product, location, lot) from the lines.
  const effects: QuantEffect[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    const qty = line.qty ?? 0;
    if (!productId || qty <= 0) {
      continue;
    }
    const deltas = computeLineQuantDeltas(
      picking.kind,
      qty,
      line.src_location_id,
      line.dst_location_id,
    );
    for (const d of deltas) {
      effects.push({
        productId,
        locationId: d.locationId,
        delta: d.delta,
        lotId: line.lot_id ?? null,
      });
    }
  }

  const aggregated = aggregateQuantDeltas(effects);
  const now = nowISO();
  let moveCount = 0;

  // Pre-flight OUTSIDE the transaction: the operation itself (`picking.id`) and
  // every quant the lines will touch, in parallel. See the file header — the
  // internal SELECT below remains unchanged.
  await Promise.all([
    waitForRowPresent(db, stock_picking, stock_picking.id, picking.id, options),
    ...[...aggregated.values()]
      .filter(({ delta }) => delta !== 0)
      .map(({ productId, locationId, lotId }) =>
        readMatchingRowVerified(
          () =>
            db
              .select({ id: stock_quant.id })
              .from(stock_quant)
              .where(quantMatch(productId, locationId, lotId)),
          options,
        ),
      ),
  ]);

  await db.transaction(async (trx) => {
    for (const { productId, locationId, delta, lotId } of aggregated.values()) {
      if (delta === 0) {
        continue;
      }
      // append-only stock_move (audit). dst = location with delta>0,
      // src = location with delta<0 (one quant per entry).
      await trx.insert(stock_move).values({
        id: generateId(),
        organization_id: orgId,
        product_id: productId,
        src_location_id: delta < 0 ? locationId : null,
        dst_location_id: delta > 0 ? locationId : null,
        qty: delta,
        reason: picking.kind,
        picking_id: picking.id,
        lot_id: lotId,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;

      const [existing] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotId));

      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateId(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: applyMove(0, delta),
          lot_id: lotId,
          created_at: now,
          updated_at: now,
        });
      }
    }

    await trx
      .update(stock_picking)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(stock_picking.id, picking.id));
  });

  return { confirmed: true, moveCount };
}

/**
 * Sends an operation to trash (L2, soft-delete). Its lines are PRESERVED (hidden
 * by the operation filter) and reappear when restored. Moves already materialized
 * by a confirmation are not touched (append-only): deleting a completed picking
 * does NOT undo its stock.
 */
export async function deletePickingMutation(
  db: PowerSyncDrizzleDB,
  pickingId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    stock_picking,
    stock_picking.id,
    pickingId,
    { deleted_at: nowISO() },
    options,
  );
}

/** Restores an operation from trash (its lines reappear with it). */
export async function restorePickingMutation(
  db: PowerSyncDrizzleDB,
  pickingId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    stock_picking,
    stock_picking.id,
    pickingId,
    { deleted_at: null },
    options,
  );
}

/** Restores a warehouse from trash. */
export async function restoreWarehouseMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, warehouse, warehouse.id, id, { deleted_at: null }, options);
}

/** Restores a location from trash. */
export async function restoreLocationMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, location, location.id, id, { deleted_at: null }, options);
}

// ─── REPLENISHMENT (Stockpilot F2) ──────────────────────────────────────────

export interface CreateReorderRuleInput {
  productId: string;
  warehouseId?: string | null;
  minQty: number;
  maxQty: number;
}

/** Creates a replenishment rule (min/max per product×warehouse). */
export async function createReorderRuleMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateReorderRuleInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(reorder_rule).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    warehouse_id: input.warehouseId ?? null,
    min_qty: input.minQty,
    max_qty: input.maxQty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a replenishment rule. */
export async function deleteReorderRuleMutation(
  db: PowerSyncDrizzleDB,
  ruleId: string,
): Promise<void> {
  await db.delete(reorder_rule).where(eq(reorder_rule.id, ruleId));
}

// ─── PURCHASING: VENDORS (Stockpilot F3) ────────────────────────────────────

export interface CreateVendorInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  leadTimeDays?: number;
}

/** Creates a vendor. */
export async function createVendorMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateVendorInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(vendor).values({
    id,
    organization_id: orgId,
    name: input.name.trim(),
    email: input.email?.trim() ?? null,
    phone: input.phone?.trim() ?? null,
    lead_time_days: input.leadTimeDays ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a vendor. */
export async function deleteVendorMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(vendor).where(eq(vendor.id, id));
}

export interface SetSupplierPriceInput {
  productId: string;
  vendorId: string;
  /** Purchase price per whole unit, in minor currency units. */
  price: number;
  vendorCode?: string | null;
  minQty?: number;
  leadTimeDays?: number;
}

/** Sets the vendor-product price (vendor pricelist). */
export async function setSupplierPriceMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: SetSupplierPriceInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(product_supplier).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    vendor_id: input.vendorId,
    vendor_code: input.vendorCode?.trim() ?? null,
    price: input.price,
    min_qty: input.minQty ?? 0,
    lead_time_days: input.leadTimeDays ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a vendor-product price. */
export async function deleteSupplierPriceMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(product_supplier).where(eq(product_supplier.id, id));
}

// ─── PURCHASING: PURCHASE ORDERS (Stockpilot F3) ─────────────────────────────

export interface CreatePurchaseOrderInput {
  vendorId?: string | null;
  warehouseId?: string | null;
  dstLocationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Creates a purchase order in draft state. */
export async function createPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePurchaseOrderInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(purchase_order).values({
    id,
    organization_id: orgId,
    vendor_id: input.vendorId ?? null,
    warehouse_id: input.warehouseId ?? null,
    dst_location_id: input.dstLocationId ?? null,
    state: "draft",
    reference: input.reference?.trim() ?? null,
    note: input.note?.trim() ?? null,
    confirmed_at: null,
    received_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Adds a line to a draft purchase order. */
export async function addPurchaseOrderLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  poId: string,
  productId: string,
  qty: number,
  price: number,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(purchase_order_line).values({
    id,
    organization_id: orgId,
    po_id: poId,
    product_id: productId,
    qty,
    price,
    received_qty: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a line from a draft purchase order. */
export async function deletePurchaseOrderLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(purchase_order_line).where(eq(purchase_order_line.id, lineId));
}

/** Deletes a purchase order and its lines. */
export async function deletePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  poId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(purchase_order_line).where(eq(purchase_order_line.po_id, poId));
    await trx.delete(purchase_order).where(eq(purchase_order.id, poId));
  });
}

interface PurchaseOrderState {
  id: string;
  state: string | null;
}

/** Confirms a purchase order (draft→confirmed). CC-7: blocking. */
export async function confirmPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  poId: string,
  po: PurchaseOrderState,
  options?: VerifiedWriteOptions,
): Promise<{ confirmed: boolean }> {
  if (!canConfirm(po.state)) {
    return { confirmed: false };
  }
  await patchRowVerified(
    db,
    purchase_order,
    purchase_order.id,
    poId,
    { state: "confirmed", confirmed_at: nowISO() },
    options,
  );
  return { confirmed: true };
}

/**
 * Receives a purchase order (fully or partially). Generates a receipt
 * (stock_picking of type 'receipt') with one stock_move per line, increases
 * on-hand (stock_quant), creates AVCO valuation layers (valuation_layer) at each
 * line's purchase cost, and updates received_qty. If anything remains pending,
 * the order stays 'confirmed' (BACKORDER); once all is received, it becomes
 * 'received'.
 *
 * CC-6: AVCO/matching calculation is pure (./valuation, ./purchases).
 * CC-7: everything blocks (await) in a single transaction.
 *
 * @param requested  lineId → quantity to receive now (minor units). If undefined,
 *                   receives ALL pending quantity for each line.
 */
export async function receivePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  po: {
    id: string;
    state: string | null;
    warehouse_id: string | null;
    dst_location_id: string | null;
    reference: string | null;
  },
  lines: IdentifiedPoLine[],
  requested?: Map<string, number>,
  options?: VerifiedWriteOptions,
): Promise<{ received: boolean; pickingId: string | null; backorder: boolean }> {
  if (!canReceive(po.state)) {
    return { received: false, pickingId: null, backorder: false };
  }

  const dstLocationId = po.dst_location_id;
  if (!dstLocationId) {
    // Without a destination location, the receipt cannot be materialized.
    return { received: false, pickingId: null, backorder: false };
  }

  const allocations = planReceipt(lines, requested);
  if (allocations.length === 0) {
    return { received: false, pickingId: null, backorder: false };
  }

  const now = nowISO();
  const pickingId = generateId();
  // lineId → quantity received in this receipt, for updating lines.
  const receivedByLine = new Map<string, number>();
  if (requested) {
    for (const line of lines) {
      const pending = Math.max((line.qty ?? 0) - (line.received_qty ?? 0), 0);
      const ask = Math.min(Math.max(requested.get(line.id) ?? 0, 0), pending);
      if (ask > 0) {
        receivedByLine.set(line.id, ask);
      }
    }
  } else {
    for (const line of lines) {
      const pending = Math.max((line.qty ?? 0) - (line.received_qty ?? 0), 0);
      if (pending > 0) {
        receivedByLine.set(line.id, pending);
      }
    }
  }

  // Pre-flight OUTSIDE the transaction: the order (`po.id`), lines receiving
  // quantity (`purchase_order_line`, known IDs), and every quant the allocations
  // will touch (content-based, no known ID), in parallel. See the file header —
  // internal SELECTs/UPDATEs remain unchanged.
  await Promise.all([
    waitForRowPresent(db, purchase_order, purchase_order.id, po.id, options),
    ...lines
      .filter((line) => receivedByLine.has(line.id))
      .map((line) =>
        waitForRowPresent(db, purchase_order_line, purchase_order_line.id, line.id, options),
      ),
    ...allocations.map((alloc) =>
      readMatchingRowVerified(
        () =>
          db
            .select({ id: stock_quant.id })
            .from(stock_quant)
            .where(
              and(
                eq(stock_quant.product_id, alloc.productId),
                eq(stock_quant.location_id, dstLocationId),
              ),
            ),
        options,
      ),
    ),
  ]);

  await db.transaction(async (trx) => {
    // 1) Receipt document (stock_picking of type 'receipt') already confirmed.
    await trx.insert(stock_picking).values({
      id: pickingId,
      organization_id: orgId,
      kind: "receipt",
      state: "done",
      reference: po.reference ?? null,
      warehouse_id: po.warehouse_id ?? null,
      src_location_id: null,
      dst_location_id: dstLocationId,
      note: null,
      done_at: now,
      created_at: now,
      updated_at: now,
    });

    // 2) Accumulated AVCO state per product (from existing layers).
    const avcoByProduct = new Map<string, AvcoState>();

    for (const alloc of allocations) {
      // 2a) stock_move + stock_move_line (inbound to the destination location).
      const moveId = generateId();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: alloc.productId,
        src_location_id: null,
        dst_location_id: dstLocationId,
        qty: alloc.qty,
        reason: "receipt",
        picking_id: pickingId,
        created_at: now,
        updated_at: now,
      });
      await trx.insert(stock_move_line).values({
        id: generateId(),
        organization_id: orgId,
        picking_id: pickingId,
        product_id: alloc.productId,
        src_location_id: null,
        dst_location_id: dstLocationId,
        qty: alloc.qty,
        created_at: now,
        updated_at: now,
      });

      // 2b) on-hand (stock_quant) at the destination location.
      const [existingQuant] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(
          and(
            eq(stock_quant.product_id, alloc.productId),
            eq(stock_quant.location_id, dstLocationId),
          ),
        );
      if (existingQuant) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existingQuant.qty ?? 0, alloc.qty), updated_at: now })
          .where(eq(stock_quant.id, existingQuant.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateId(),
          organization_id: orgId,
          product_id: alloc.productId,
          location_id: dstLocationId,
          qty: alloc.qty,
          created_at: now,
          updated_at: now,
        });
      }

      // 2c) AVCO valuation layer. Initial state = sum of prior layers.
      let state = avcoByProduct.get(alloc.productId);
      if (!state) {
        const prior = await trx
          .select({ qty: valuation_layer.qty, value: valuation_layer.value })
          .from(valuation_layer)
          .where(eq(valuation_layer.product_id, alloc.productId))
          .orderBy(asc(valuation_layer.created_at));
        state = prior.reduce<AvcoState>(
          (acc, l) => ({ qty: acc.qty + (l.qty ?? 0), value: acc.value + (l.value ?? 0) }),
          { qty: 0, value: 0 },
        );
      }
      const layer = applyInbound(state, alloc.qty, alloc.unitCost);
      avcoByProduct.set(alloc.productId, layer.state);
      await trx.insert(valuation_layer).values({
        id: generateId(),
        organization_id: orgId,
        product_id: alloc.productId,
        move_id: moveId,
        warehouse_id: po.warehouse_id ?? null,
        qty: layer.qty,
        unit_cost: layer.unitCost,
        value: layer.value,
        created_at: now,
        updated_at: now,
      });
    }

    // 3) Update received_qty for touched lines.
    const linesAfter = lines.map((l) => ({
      ...l,
      received_qty: (l.received_qty ?? 0) + (receivedByLine.get(l.id) ?? 0),
    }));
    for (const line of linesAfter) {
      if (receivedByLine.has(line.id)) {
        await trx
          .update(purchase_order_line)
          .set({ received_qty: line.received_qty, updated_at: now })
          .where(eq(purchase_order_line.id, line.id));
      }
    }

    // 4) Resolve order state (received vs backorder).
    const nextState = resolvePoStateAfterReceipt(linesAfter);
    await trx
      .update(purchase_order)
      .set({
        state: nextState,
        received_at: nextState === "received" ? now : null,
        updated_at: now,
      })
      .where(eq(purchase_order.id, po.id));
  });

  const totalPending = lines.reduce((acc, l) => {
    const after = (l.received_qty ?? 0) + (receivedByLine.get(l.id) ?? 0);
    return acc + Math.max((l.qty ?? 0) - after, 0);
  }, 0);

  return { received: true, pickingId, backorder: totalPending > 0 };
}

// ─── TRACEABILITY: LOTS / SERIALS (Stockpilot F4) ────────────────────────────

export interface CreateLotInput {
  productId: string;
  name: string;
  /** ISO expiry date (YYYY-MM-DD), or null if it does not expire. */
  expiryDate?: string | null;
}

/** Creates a product lot / serial number. */
export async function createLotMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateLotInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(lot).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    name: input.name.trim(),
    expiry_date: input.expiryDate?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Updates a lot (name / expiry). */
export async function updateLotMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string; expiry_date: string | null }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, lot, lot.id, id, finalUpdates, options);
}

/** Deletes a lot. */
export async function deleteLotMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(lot).where(eq(lot.id, id));
}

// ─── CYCLE COUNT / PHYSICAL INVENTORY (Stockpilot F4) ────────────────────────

export interface CreateInventoryCountInput {
  locationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Creates a count document in draft state. */
export async function createInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateInventoryCountInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(inventory_count).values({
    id,
    organization_id: orgId,
    location_id: input.locationId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Adds a count line. Captures the current THEORETICAL on-hand for
 * (product×location×lot) as the reference delta when validating.
 */
export async function addInventoryCountLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  count: { id: string; location_id: string | null },
  productId: string,
  countedQty: number,
  lotId?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  const lotRef = lotId ?? null;

  let theoretical = 0;
  if (count.location_id) {
    const [existing] = await db
      .select({ qty: stock_quant.qty })
      .from(stock_quant)
      .where(quantMatch(productId, count.location_id, lotRef));
    theoretical = existing?.qty ?? 0;
  }

  await db.insert(inventory_count_line).values({
    id,
    organization_id: orgId,
    count_id: count.id,
    product_id: productId,
    lot_id: lotRef,
    theoretical_qty: theoretical,
    counted_qty: countedQty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a count line (draft). */
export async function deleteInventoryCountLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(inventory_count_line).where(eq(inventory_count_line.id, lineId));
}

interface ValidatableCount {
  id: string;
  state: string | null;
  location_id: string | null;
}

/**
 * Validates a count (draft→validated): for each line with a difference
 * (counted − theoretical ≠ 0), materializes an adjustment stock_move and balances
 * derived on-hand (stock_quant) by (product×location×lot). CC-6: the adjustment
 * plan is pure (./counts). CC-7: blocking (await) in a single transaction.
 * Idempotent: if already validated, does nothing.
 */
export async function validateInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  count: ValidatableCount,
  lines: CountLineLike[],
  options?: VerifiedWriteOptions,
): Promise<{ validated: boolean; moveCount: number }> {
  if (count.state === "done") {
    return { validated: false, moveCount: 0 };
  }
  const locationId = count.location_id;
  if (!locationId) {
    return { validated: false, moveCount: 0 };
  }

  const adjustments = planCountAdjustments(lines);
  const now = nowISO();
  let moveCount = 0;

  // Pre-flight OUTSIDE the transaction: the count document (`count.id`) and
  // every quant the adjustments will touch, in parallel. See the file header —
  // internal SELECTs/UPDATEs remain unchanged.
  await Promise.all([
    waitForRowPresent(db, inventory_count, inventory_count.id, count.id, options),
    ...adjustments.map(({ productId, lotId }) =>
      readMatchingRowVerified(
        () =>
          db
            .select({ id: stock_quant.id })
            .from(stock_quant)
            .where(quantMatch(productId, locationId, lotId)),
        options,
      ),
    ),
  ]);

  await db.transaction(async (trx) => {
    for (const { productId, lotId, delta, newOnHand } of adjustments) {
      // adjustment stock_move (append-only). dst for delta>0, src for delta<0.
      await trx.insert(stock_move).values({
        id: generateId(),
        organization_id: orgId,
        product_id: productId,
        src_location_id: delta < 0 ? locationId : null,
        dst_location_id: delta > 0 ? locationId : null,
        qty: delta,
        reason: "count-adjustment",
        lot_id: lotId,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;

      const [existing] = await trx
        .select({ id: stock_quant.id })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotId));

      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: newOnHand, updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateId(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: newOnHand,
          lot_id: lotId,
          created_at: now,
          updated_at: now,
        });
      }
    }

    await trx
      .update(inventory_count)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(inventory_count.id, count.id));
  });

  return { validated: true, moveCount };
}

/** Deletes a count document and its lines. */
export async function deleteInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  countId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(inventory_count_line).where(eq(inventory_count_line.count_id, countId));
    await trx.delete(inventory_count).where(eq(inventory_count.id, countId));
  });
}

// ─── MANUFACTURING + LANDED COSTS + RETURNS (Stockpilot F5) ──────────────────

/**
 * Drizzle transaction type (the object that receives the `db.transaction`
 * callback). Narrower than `PowerSyncDrizzleDB` (without `db`/`watch`), just
 * enough for the F5 helpers that run inside a transaction.
 */
type DrizzleTx = Parameters<Parameters<PowerSyncDrizzleDB["transaction"]>[0]>[0];

/**
 * Reads a product's accumulated AVCO state (sum of valuation layers) under a
 * transaction. Reused by all F5 cost paths.
 */
async function readAvcoState(trx: DrizzleTx, productId: string): Promise<AvcoState> {
  const prior = await trx
    .select({ qty: valuation_layer.qty, value: valuation_layer.value })
    .from(valuation_layer)
    .where(eq(valuation_layer.product_id, productId))
    .orderBy(asc(valuation_layer.created_at));
  return prior.reduce<AvcoState>(
    (acc, l) => ({ qty: acc.qty + (l.qty ?? 0), value: acc.value + (l.value ?? 0) }),
    { qty: 0, value: 0 },
  );
}

/** Applies a delta to a (product×location) stock_quant, creating it if missing. */
async function applyQuantDelta(
  trx: DrizzleTx,
  orgId: string,
  productId: string,
  locationId: string,
  delta: number,
  now: string,
): Promise<void> {
  const [existing] = await trx
    .select({ id: stock_quant.id, qty: stock_quant.qty })
    .from(stock_quant)
    .where(quantMatch(productId, locationId, null));
  if (existing) {
    await trx
      .update(stock_quant)
      .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
      .where(eq(stock_quant.id, existing.id));
  } else {
    await trx.insert(stock_quant).values({
      id: generateId(),
      organization_id: orgId,
      product_id: productId,
      location_id: locationId,
      qty: applyMove(0, delta),
      created_at: now,
      updated_at: now,
    });
  }
}

// ─── BILL OF MATERIALS (BoM) ─────────────────────────────────────────────────

export interface CreateBomInput {
  productId: string;
  /** Finished-goods quantity produced by one recipe batch (minor units). */
  qty?: number;
  reference?: string | null;
}

/** Creates a bill of materials (recipe) for a finished product. */
export async function createBomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateBomInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(bill_of_materials).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    qty: input.qty ?? QTY_SCALE,
    reference: input.reference?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Adds a component (product × quantity) to a BoM. */
export async function addBomComponentMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  bomId: string,
  productId: string,
  qty: number,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(bom_component).values({
    id,
    organization_id: orgId,
    bom_id: bomId,
    product_id: productId,
    qty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a component from a BoM. */
export async function deleteBomComponentMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(bom_component).where(eq(bom_component.id, id));
}

/** Deletes a BoM and its components. */
export async function deleteBomMutation(db: PowerSyncDrizzleDB, bomId: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(bom_component).where(eq(bom_component.bom_id, bomId));
    await trx.delete(bill_of_materials).where(eq(bill_of_materials.id, bomId));
  });
}

// ─── MANUFACTURING ORDERS ────────────────────────────────────────────────────

export interface CreateManufacturingOrderInput {
  bomId?: string | null;
  productId: string;
  /** Finished-goods quantity to manufacture (minor units). */
  qty: number;
  locationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Creates a manufacturing order in draft state. */
export async function createManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateManufacturingOrderInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(manufacturing_order).values({
    id,
    organization_id: orgId,
    bom_id: input.bomId ?? null,
    product_id: input.productId,
    qty: input.qty,
    location_id: input.locationId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    produced_cost: 0,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a draft manufacturing order. */
export async function deleteManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(manufacturing_order).where(eq(manufacturing_order.id, id));
}

interface ProducibleOrder {
  id: string;
  state: string | null;
  product_id: string | null;
  qty: number | null;
  bom_qty: number;
  location_id: string | null;
  warehouse_id?: string | null;
}

/**
 * Manufactures an order (draft→done): CONSUMES components (outbound stock_move
 * + negative AVCO layer at the current average cost) and PRODUCE the finished
 * goods (inbound stock_move + positive AVCO layer), whose unit cost is DERIVED
 * from the sum of consumed component costs.
 *
 * CC-6: the consumption plan and derived cost are pure (./manufacturing).
 * CC-7: everything blocks (await) in a single transaction. Idempotent: if it is
 * already 'done', does nothing.
 */
export async function produceManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  order: ProducibleOrder,
  components: BomComponentLike[],
  options?: VerifiedWriteOptions,
): Promise<{ produced: boolean; producedUnitCost: number }> {
  if (!canProduce(order.state)) {
    return { produced: false, producedUnitCost: 0 };
  }
  const productId = order.product_id;
  const locationId = order.location_id;
  const orderQty = order.qty ?? 0;
  if (!productId || !locationId || orderQty <= 0) {
    return { produced: false, producedUnitCost: 0 };
  }

  const consumption = planComponentConsumption(components, orderQty, order.bom_qty);
  const now = nowISO();
  let unitCost = 0;

  // Pre-flight OUTSIDE the transaction: the order (`order.id`) and every quant
  // that component consumption + finished-goods production will touch
  // (`applyQuantDelta`, content-based without lot), in parallel. See the file
  // header — the rest of the transaction remains unchanged.
  await Promise.all([
    waitForRowPresent(db, manufacturing_order, manufacturing_order.id, order.id, options),
    ...consumption.map((c) =>
      readMatchingRowVerified(
        () =>
          db
            .select({ id: stock_quant.id })
            .from(stock_quant)
            .where(quantMatch(c.productId, locationId, null)),
        options,
      ),
    ),
    readMatchingRowVerified(
      () =>
        db
          .select({ id: stock_quant.id })
          .from(stock_quant)
          .where(quantMatch(productId, locationId, null)),
      options,
    ),
  ]);

  await db.transaction(async (trx) => {
    // 1) Consume components: outbound + negative AVCO layer at the current average.
    let consumedValue = 0;
    for (const c of consumption) {
      const state = await readAvcoState(trx, c.productId);
      const avg = averageUnitCost(state);
      const value = valueOf(c.qty, avg);
      consumedValue += value;

      const moveId = generateId();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: c.productId,
        src_location_id: locationId,
        dst_location_id: null,
        qty: -c.qty,
        reason: "mfg-consume",
        picking_id: order.id,
        created_at: now,
        updated_at: now,
      });
      await applyQuantDelta(trx, orgId, c.productId, locationId, -c.qty, now);
      await trx.insert(valuation_layer).values({
        id: generateId(),
        organization_id: orgId,
        product_id: c.productId,
        move_id: moveId,
        warehouse_id: order.warehouse_id ?? null,
        qty: -c.qty || 0,
        unit_cost: avg,
        value: -value || 0,
        created_at: now,
        updated_at: now,
      });
    }

    // 2) Finished-goods unit cost derived from consumed value.
    unitCost = producedUnitCost(consumedValue, orderQty);

    // 3) Produce finished goods: inbound + positive AVCO layer at the derived cost.
    const finishedMoveId = generateId();
    await trx.insert(stock_move).values({
      id: finishedMoveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: null,
      dst_location_id: locationId,
      qty: orderQty,
      reason: "mfg-produce",
      picking_id: order.id,
      created_at: now,
      updated_at: now,
    });
    await applyQuantDelta(trx, orgId, productId, locationId, orderQty, now);
    await trx.insert(valuation_layer).values({
      id: generateId(),
      organization_id: orgId,
      product_id: productId,
      move_id: finishedMoveId,
      warehouse_id: order.warehouse_id ?? null,
      qty: orderQty,
      unit_cost: unitCost,
      value: valueOf(orderQty, unitCost),
      created_at: now,
      updated_at: now,
    });

    await trx
      .update(manufacturing_order)
      .set({ state: "done", produced_cost: unitCost, done_at: now, updated_at: now })
      .where(eq(manufacturing_order.id, order.id));
  });

  return { produced: true, producedUnitCost: unitCost };
}

// ─── LANDED COSTS ────────────────────────────────────────────────────────────

export interface CreateLandedCostInput {
  pickingId: string;
  /** Total additional cost to allocate (minor currency units). */
  amount: number;
  reference?: string | null;
  note?: string | null;
}

/** Creates a landed cost in draft state for a receipt. */
export async function createLandedCostMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateLandedCostInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(landed_cost).values({
    id,
    organization_id: orgId,
    picking_id: input.pickingId,
    amount: input.amount,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a draft landed cost. */
export async function deleteLandedCostMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(landed_cost).where(eq(landed_cost.id, id));
}

interface ApplicableLandedCost {
  id: string;
  state: string | null;
  picking_id: string | null;
  amount: number | null;
  warehouse_id?: string | null;
}

/**
 * Applies a landed cost (draft→applied): allocates the additional cost across the
 * receipt's products PROPORTIONALLY to received value and creates, for each
 * product, a VALUE-ONLY valuation layer (qty=0) that revalues inventory
 * (increasing moving-average cost without touching on-hand).
 *
 * CC-6: allocation is pure (./landed-cost). CC-7: blocking in one transaction.
 * Idempotent: if it is already 'done', does nothing.
 */
export async function applyLandedCostMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  lc: ApplicableLandedCost,
  options?: VerifiedWriteOptions,
): Promise<{ applied: boolean; allocationCount: number }> {
  if (!canApplyLandedCost(lc.state)) {
    return { applied: false, allocationCount: 0 };
  }
  const pickingId = lc.picking_id;
  const amount = lc.amount ?? 0;
  if (!pickingId || amount === 0) {
    return { applied: false, allocationCount: 0 };
  }

  // Allocation basis: value received per product in that receipt (layers whose
  // move belongs to the picking). It is aggregated by product.
  const layers = await db
    .select({
      product_id: valuation_layer.product_id,
      value: valuation_layer.value,
      move_id: valuation_layer.move_id,
    })
    .from(valuation_layer)
    .innerJoin(stock_move, eq(valuation_layer.move_id, stock_move.id))
    .where(eq(stock_move.picking_id, pickingId));

  const byProduct = new Map<string, number>();
  for (const l of layers) {
    const pid = l.product_id;
    if (!pid) {
      continue;
    }
    byProduct.set(pid, (byProduct.get(pid) ?? 0) + (l.value ?? 0));
  }
  const bases: LandedCostBase[] = [...byProduct.entries()].map(([productId, receiptValue]) => ({
    productId,
    receiptValue,
  }));
  if (bases.length === 0) {
    return { applied: false, allocationCount: 0 };
  }

  const allocations = allocateLandedCost(amount, bases);
  const now = nowISO();
  let allocationCount = 0;

  // Pre-flight OUTSIDE the transaction: the landed cost (`lc.id`) that the
  // transaction will mark 'done'. See the file header.
  await waitForRowPresent(db, landed_cost, landed_cost.id, lc.id, options);

  await db.transaction(async (trx) => {
    for (const alloc of allocations) {
      if (alloc.amount === 0) {
        continue;
      }
      // Revaluation layer: qty=0 (does not touch on-hand), value=allocated share.
      const layerId = generateId();
      await trx.insert(valuation_layer).values({
        id: layerId,
        organization_id: orgId,
        product_id: alloc.productId,
        move_id: null,
        warehouse_id: lc.warehouse_id ?? null,
        qty: 0,
        unit_cost: 0,
        value: alloc.amount,
        created_at: now,
        updated_at: now,
      });
      await trx.insert(landed_cost_line).values({
        id: generateId(),
        organization_id: orgId,
        landed_cost_id: lc.id,
        product_id: alloc.productId,
        valuation_layer_id: layerId,
        amount: alloc.amount,
        created_at: now,
        updated_at: now,
      });
      allocationCount += 1;
    }

    await trx
      .update(landed_cost)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(landed_cost.id, lc.id));
  });

  return { applied: true, allocationCount };
}

// ─── VENDOR RETURNS ─────────────────────────────────────────────────────────

export interface CreatePurchaseReturnInput {
  poId?: string | null;
  srcLocationId?: string | null;
  warehouseId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Creates a vendor return in draft state. */
export async function createPurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePurchaseReturnInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(purchase_return).values({
    id,
    organization_id: orgId,
    po_id: input.poId ?? null,
    src_location_id: input.srcLocationId ?? null,
    warehouse_id: input.warehouseId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Adds a line (product × quantity) to a draft return. */
export async function addPurchaseReturnLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  returnId: string,
  productId: string,
  qty: number,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(purchase_return_line).values({
    id,
    organization_id: orgId,
    return_id: returnId,
    product_id: productId,
    qty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Deletes a line from a draft return. */
export async function deletePurchaseReturnLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(purchase_return_line).where(eq(purchase_return_line.id, lineId));
}

/** Deletes a return and its lines. */
export async function deletePurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  returnId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(purchase_return_line).where(eq(purchase_return_line.return_id, returnId));
    await trx.delete(purchase_return).where(eq(purchase_return.id, returnId));
  });
}

interface ConfirmableReturn {
  id: string;
  state: string | null;
  src_location_id: string | null;
  warehouse_id: string | null;
}

/**
 * Confirms a vendor return (draft→done): for each line, removes stock from
 * on-hand (outbound stock_move) and records a NEGATIVE valuation layer at the
 * current moving-average cost. CC-6: the plan is pure (./returns).
 * CC-7: blocking in one transaction. Idempotent: if already 'done', does nothing.
 */
export async function confirmPurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  ret: ConfirmableReturn,
  lines: ReturnLineLike[],
  options?: VerifiedWriteOptions,
): Promise<{ confirmed: boolean; moveCount: number }> {
  if (!canConfirmReturn(ret.state)) {
    return { confirmed: false, moveCount: 0 };
  }
  const locationId = ret.src_location_id;
  if (!locationId) {
    return { confirmed: false, moveCount: 0 };
  }

  const effects = planReturnEffects(lines);
  const now = nowISO();
  let moveCount = 0;

  // Pre-flight OUTSIDE the transaction: the return (`ret.id`) and every quant
  // the effects will touch (`applyQuantDelta`, content-based without lot), in
  // parallel. See the file header.
  await Promise.all([
    waitForRowPresent(db, purchase_return, purchase_return.id, ret.id, options),
    ...effects.map((eff) =>
      readMatchingRowVerified(
        () =>
          db
            .select({ id: stock_quant.id })
            .from(stock_quant)
            .where(quantMatch(eff.productId, locationId, null)),
        options,
      ),
    ),
  ]);

  await db.transaction(async (trx) => {
    for (const eff of effects) {
      const state = await readAvcoState(trx, eff.productId);
      const avg = averageUnitCost(state);
      const value = valueOf(eff.qty, avg);

      const moveId = generateId();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: eff.productId,
        src_location_id: locationId,
        dst_location_id: null,
        qty: -eff.qty,
        reason: "purchase-return",
        picking_id: ret.id,
        created_at: now,
        updated_at: now,
      });
      await applyQuantDelta(trx, orgId, eff.productId, locationId, -eff.qty, now);
      await trx.insert(valuation_layer).values({
        id: generateId(),
        organization_id: orgId,
        product_id: eff.productId,
        move_id: moveId,
        warehouse_id: ret.warehouse_id ?? null,
        qty: -eff.qty || 0,
        unit_cost: avg,
        value: -value || 0,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;
    }

    await trx
      .update(purchase_return)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(purchase_return.id, ret.id));
  });

  return { confirmed: true, moveCount };
}
